import { useCallback, useEffect, useSyncExternalStore } from "react";

/**
 * Session cache of employee profile photos as blob object URLs.
 *
 * Photos are fetched through the host app's authenticated API client (injected with
 * configureAvatarFetcher — this package has no HTTP client of its own) instead of a raw `<img src>`
 * to the API. An `<img>` cannot send the bearer token, which forced GET /Profile/Avatar to be
 * anonymous. Reads are synchronous (getAvatarUrl) so static-markup renderers like the org chart can
 * use them; React components use useAvatarUrl.
 */

/**
 * Resolves to the image, or null when the employee has no viewable photo. Resolves to undefined, or
 * rejects, when the photo may become available (malware scan pending, rate limited, server error):
 * that is retried with a delay instead of being cached. `version` changes after the photo is replaced;
 * send it with the request so the browser cannot answer from its HTTP cache.
 */
export type AvatarFetcher = (
  employeeId: string,
  version: number,
) => Promise<Blob | null | undefined>;

// Uploads are not resized server-side (up to 10 MB each), so bound how many blobs stay in memory.
// The least recently read photo is evicted first; it is fetched again when next rendered. Keep this
// well above what one screen shows, or an org chart redraw would refetch what it just evicted.
const MAX_CACHED = 1000;
// Covers a malware scan (usually well under a minute) without polling forever.
const RETRY_DELAYS_MS = [3000, 5000, 10000, 30000, 30000, 60000];

let fetcher: AvatarFetcher | null = null;
// employeeId -> object URL, or null for "no photo". Absent = not loaded yet.
const cache = new Map<string, string | null>();
const inFlight = new Map<string, Promise<void>>();
// Pending retry timer and how many retries have been made, per employee.
const retries = new Map<string, ReturnType<typeof setTimeout>>();
const retryAttempts = new Map<string, number>();
const versions = new Map<string, number>();
const listeners = new Set<(employeeId: string) => void>();

const notify = (employeeId: string) =>
  listeners.forEach((listener) => {
    try {
      listener(employeeId);
    } catch {
      // One broken subscriber must not stop the others from updating.
    }
  });

const remove = (employeeId: string) => {
  const url = cache.get(employeeId);
  if (url) URL.revokeObjectURL(url);
  cache.delete(employeeId);
};

const store = (employeeId: string, url: string | null) => {
  cache.set(employeeId, url);
  while (cache.size > MAX_CACHED) remove(cache.keys().next().value as string);
  notify(employeeId);
};

export function configureAvatarFetcher(f: AvatarFetcher | null): void {
  fetcher = f;
}

/** Cached object URL, null when there is no photo, undefined when not loaded yet. */
export function getAvatarUrl(employeeId: string): string | null | undefined {
  const url = cache.get(employeeId);
  if (url !== undefined) {
    // Re-insert so eviction drops the least recently read photo, not one still on screen.
    cache.delete(employeeId);
    cache.set(employeeId, url);
  }
  return url;
}

/** Starts loading a photo unless it is cached, already loading, or waiting to retry. */
export function loadAvatar(employeeId: string): void {
  if (
    !fetcher ||
    cache.has(employeeId) ||
    inFlight.has(employeeId) ||
    retries.has(employeeId)
  )
    return;

  const request = fetcher(employeeId, versions.get(employeeId) ?? 0)
    .catch(() => undefined)
    .then((blob) => {
      // Dropped while in flight (invalidateAvatar): this response may predate an upload.
      if (inFlight.get(employeeId) !== request) return;
      inFlight.delete(employeeId);
      if (blob !== undefined) {
        retryAttempts.delete(employeeId);
        return store(employeeId, blob ? URL.createObjectURL(blob) : null);
      }

      const attempt = retryAttempts.get(employeeId) ?? 0;
      if (attempt >= RETRY_DELAYS_MS.length) {
        retryAttempts.delete(employeeId);
        return store(employeeId, null);
      }
      retryAttempts.set(employeeId, attempt + 1);
      const timer = setTimeout(() => {
        if (retries.get(employeeId) !== timer) return;
        retries.delete(employeeId);
        loadAvatar(employeeId);
      }, RETRY_DELAYS_MS[attempt]);
      retries.set(employeeId, timer);
    });
  inFlight.set(employeeId, request);
}

/** Forgets a photo (e.g. after an upload) so the next render fetches the new one. */
export function invalidateAvatar(employeeId: string): void {
  inFlight.delete(employeeId);
  clearTimeout(retries.get(employeeId));
  retries.delete(employeeId);
  retryAttempts.delete(employeeId);
  versions.set(employeeId, (versions.get(employeeId) ?? 0) + 1);
  remove(employeeId);
  notify(employeeId);
}

/** Listener receives the employeeId whose photo changed. Returns an unsubscribe function. */
export function subscribeAvatars(
  listener: (employeeId: string) => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Object URL for the employee's photo, or null while loading / when there is none.
 * Pass no id (or enabled=false) when the person has no photo, so nothing is requested.
 */
export function useAvatarUrl(
  employeeId: string | null | undefined,
  enabled = true,
): string | null {
  const id = enabled && employeeId ? employeeId : null;

  const subscribe = useCallback(
    (onChange: () => void) =>
      subscribeAvatars((changed) => changed === id && onChange()),
    [id],
  );
  // undefined (not loaded) is kept distinct from null (no photo) so an invalidation re-triggers the load.
  const cached = useSyncExternalStore(subscribe, () =>
    id ? getAvatarUrl(id) : null,
  );

  useEffect(() => {
    if (id && cached === undefined) loadAvatar(id);
  }, [id, cached]);

  return cached ?? null;
}
